import { useState, useEffect } from 'react';
import { message, Modal } from 'antd';
import { createEmptyClauseForm, createEmptyClauseCombinationForm, mapClauseItemToForm, mapClauseSetToForm, deepEqual } from '../utils';

export const useClauseManager = (callAction) => {
  const [clauseModalOpen, setClauseModalOpen] = useState(false);
  const [clauseManagerTab, setClauseManagerTab] = useState('clauses');
  const [clauseDefinitions, setClauseDefinitions] = useState([]);
  const [clauseDefinitionById, setClauseDefinitionById] = useState({});
  const [clauseDefinitionsLoading, setClauseDefinitionsLoading] = useState(false);
  const [clauseFieldOptions, setClauseFieldOptions] = useState([]);
  const [clauseForm, setClauseForm] = useState(createEmptyClauseForm());
  const [editingClause, setEditingClause] = useState(null);
  const [clauseSaveLoading, setClauseSaveLoading] = useState(false);
  const [clauseFormDirty, setClauseFormDirty] = useState(false);
  const [clauseListDrawerOpen, setClauseListDrawerOpen] = useState(false);
  
  const [clauseSets, setClauseSets] = useState([]);
  const [clauseSetsLoading, setClauseSetsLoading] = useState(false);
  const [editingClauseSet, setEditingClauseSet] = useState(null);
  const [clauseCombinationForm, setClauseCombinationForm] = useState(createEmptyClauseCombinationForm());
  const [clauseCombinationSaveLoading, setClauseCombinationSaveLoading] = useState(false);
  const [clauseCombinationDirty, setClauseCombinationDirty] = useState(false);

  useEffect(() => {
    const isNew = !editingClause?.id;
    const initial = isNew ? createEmptyClauseForm() : mapClauseItemToForm(editingClause);
    setClauseFormDirty(!deepEqual(initial, clauseForm));
  }, [clauseForm, editingClause]);

  useEffect(() => {
    const isNew = !editingClauseSet?.id;
    const initial = isNew ? createEmptyClauseCombinationForm() : mapClauseSetToForm(editingClauseSet);
    setClauseCombinationDirty(!deepEqual(initial, clauseCombinationForm));
  }, [clauseCombinationForm, editingClauseSet]);

  const loadClauseDefinitions = async () => {
    setClauseDefinitionsLoading(true);
    try {
      const res = await callAction('list_backtest_clauses');
      if (res?.success) {
        const data = res.data || {};
        const list = Array.isArray(data) ? data : (data.items || []);
        setClauseDefinitions(list);
        const map = {};
        list.forEach(c => { map[c.id] = c; });
        setClauseDefinitionById(map);
      }
    } catch (e) {
      message.error('Failed to load clauses');
    } finally {
      setClauseDefinitionsLoading(false);
    }
  };

  const loadClauseFieldOptions = async () => {
    try {
      const res = await callAction('list_backtest_clause_fields');
      if (res?.success) {
        const data = res.data || {};
        const list = Array.isArray(data) ? data : (data.items || []);
        setClauseFieldOptions(list);
      }
    } catch {
      // ignore
    }
  };

  const loadClauseSets = async () => {
    setClauseSetsLoading(true);
    try {
      const res = await callAction('list_backtest_clause_sets');
      if (res?.success) {
        const data = res.data || {};
        const list = Array.isArray(data) ? data : (data.items || []);
        setClauseSets(list);
      }
    } catch (e) {
      message.error('Failed to load clause sets');
    } finally {
      setClauseSetsLoading(false);
    }
  };

  const ensureClauseDataLoaded = async () => {
    await Promise.all([loadClauseDefinitions(), loadClauseFieldOptions()]);
  };

  const ensureClauseSetsLoaded = async () => {
    await loadClauseSets();
  };

  const applyClauseForm = (item) => {
    setClauseForm(mapClauseItemToForm(item));
  };

  const applyClauseCombinationForm = (item) => {
    setClauseCombinationForm(mapClauseSetToForm(item));
  };

  const resetClauseEditors = () => {
    setEditingClause(null);
    setClauseForm(createEmptyClauseForm());
    setEditingClauseSet(null);
    setClauseCombinationForm(createEmptyClauseCombinationForm());
  };

  const loadClauseForEdit = async (id) => {
    if (!id) return false;
    try {
      const res = await callAction('get_backtest_clause', { id });
      if (res?.success && res.data) {
        setEditingClause(res.data);
        applyClauseForm(res.data);
        return true;
      }
    } catch {
      message.error('Failed to load clause details');
    }
    return false;
  };

  const loadClauseSetForEdit = async (id) => {
    if (!id) return;
    try {
      const res = await callAction('get_backtest_clause_set', { id });
      if (res?.success && res.data) {
        setEditingClauseSet(res.data);
        applyClauseCombinationForm(res.data);
      }
    } catch {
      message.error('Failed to load clause set');
    }
  };

  const closeClauseModal = () => {
    setClauseModalOpen(false);
    resetClauseEditors();
  };

  const handleClauseModalCloseRequest = () => {
    if (clauseFormDirty || clauseCombinationDirty) {
      Modal.confirm({
        title: 'Unsaved changes',
        content: 'You have unsaved changes in the clause editor. Discard them?',
        onOk: closeClauseModal
      });
    } else {
      closeClauseModal();
    }
  };

  const handleSaveClause = async () => {
    if (!clauseForm.clause_field || !clauseForm.clause_field_sourcetable) {
      message.error('Field and source table are required');
      return;
    }
    setClauseSaveLoading(true);
    try {
      const payload = {
        id: clauseForm.id,
        clause_field: clauseForm.clause_field,
        clause_field_sourcetable: clauseForm.clause_field_sourcetable,
        clause_operator: clauseForm.clause_operator,
        threshold_value:
          clauseForm.threshold_value === null || clauseForm.threshold_value === undefined
            ? null
            : Number(clauseForm.threshold_value),
        threshold_field: clauseForm.threshold_field,
        threshold_sourcetable: clauseForm.threshold_sourcetable,
        clause_txt: clauseForm.clause_txt,
        clause_scope: clauseForm.clause_scope,
        trade_direction: clauseForm.trade_direction,
      };
      const res = await callAction('save_backtest_clause', payload);
      if (res?.success && res.data?.item) {
        const item = res.data.item;
        setEditingClause(item);
        applyClauseForm(item);
        await loadClauseDefinitions();
        message.success('Clause saved');
      } else {
        message.error(res?.error || 'Failed to save clause');
      }
    } catch (e) {
      message.error('Failed to save clause');
    } finally {
      setClauseSaveLoading(false);
    }
  };

  const handleDeleteClause = async () => {
    try {
      const res = await callAction('delete_backtest_clause', { id: editingClause.id, hard: false });
      if (res?.success) {
        message.success('Clause disabled');
        await loadClauseDefinitions();
        closeClauseModal();
      } else {
        message.error(res?.error || 'Failed to delete clause');
      }
    } catch (e) {
      message.error('Failed to delete clause');
    }
  };

  const handleSaveClauseCombination = async () => {
    if (!clauseCombinationForm.name) {
      message.error('Combination name is required');
      return;
    }
    if (clauseCombinationForm.clause_ids.length < 2) {
      message.error('Select at least 2 clauses for a combination');
      return;
    }
    setClauseCombinationSaveLoading(true);
    try {
      const payload = {
        id: clauseCombinationForm.id,
        name: clauseCombinationForm.name,
        description: clauseCombinationForm.description,
        clause_ids: clauseCombinationForm.clause_ids,
      };
      const res = await callAction('save_backtest_clause_set', payload);
      if (res?.success && res.data?.item) {
        const item = res.data.item;
        setEditingClauseSet(item);
        applyClauseCombinationForm(item);
        await loadClauseSets();
        message.success('Combination saved');
        return item; // Return saved item for parent to use (e.g., selecting it)
      } else {
        message.error(res?.error || 'Failed to save combination');
      }
    } catch (e) {
      message.error('Failed to save combination');
    } finally {
      setClauseCombinationSaveLoading(false);
    }
    return null;
  };

  const handleDeleteClauseCombination = async () => {
    if (!editingClauseSet?.id) return;
    try {
      const res = await callAction('delete_backtest_clause_set', { id: editingClauseSet.id });
      if (res?.success) {
        message.success('Combination deleted');
        await loadClauseSets();
        closeClauseModal();
      } else {
        message.error(res?.error || 'Failed to delete combination');
      }
    } catch {
      message.error('Failed to delete combination');
    }
  };

  const formatClauseLabel = (cl) => {
    if (!cl) return '';
    const txt = cl.clause_txt ? ` (${cl.clause_txt})` : '';
    return `${(cl.clause_field || '').toUpperCase()} ${cl.clause_operator || ''} ${
      cl.threshold_value ?? cl.threshold_field ?? ''
    }${txt}`;
  };

  return {
    clauseModalOpen,
    setClauseModalOpen,
    clauseManagerTab,
    setClauseManagerTab,
    clauseDefinitions,
    clauseDefinitionById,
    clauseDefinitionsLoading,
    clauseFieldOptions,
    clauseForm,
    setClauseForm,
    editingClause,
    setEditingClause,
    clauseSaveLoading,
    clauseFormDirty,
    clauseListDrawerOpen,
    setClauseListDrawerOpen,
    clauseSets,
    clauseSetsLoading,
    editingClauseSet,
    setEditingClauseSet,
    clauseCombinationForm,
    setClauseCombinationForm,
    clauseCombinationSaveLoading,
    clauseCombinationDirty,
    loadClauseDefinitions,
    loadClauseFieldOptions,
    loadClauseSets,
    ensureClauseDataLoaded,
    ensureClauseSetsLoaded,
    loadClauseForEdit,
    loadClauseSetForEdit,
    handleClauseModalCloseRequest,
    handleSaveClause,
    handleDeleteClause,
    handleSaveClauseCombination,
    handleDeleteClauseCombination,
    closeClauseModal,
    formatClauseLabel,
    resetClauseEditors
  };
};

